import React, { useEffect, useState } from "react";
import { Wallet, Plus, Minus, ArrowUpCircle, ArrowDownCircle, RefreshCw, X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * DealerWalletModal — Admin manages a dealer's wallet.
 * Props:
 *   dealer    – { id, user_id, ... }
 *   dealerUser – { name, mobile, ... }
 *   onClose   – callback to close modal
 */
export default function DealerWalletModal({ dealer, dealerUser, onClose }) {
  const [wallet, setWallet] = useState(null);
  const [txns, setTxns] = useState({ items: [], total: 0, page: 1, limit: 25 });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("topup"); // topup | deduct | history | audit

  // Top-up form
  const [topupAmount, setTopupAmount] = useState("");
  const [topupNote, setTopupNote] = useState("");
  const [topupLoading, setTopupLoading] = useState(false);

  // Deduct form
  const [deductAmount, setDeductAmount] = useState("");
  const [deductReason, setDeductReason] = useState("");
  const [deductLoading, setDeductLoading] = useState(false);

  // Reconciliation
  const [reconcileReport, setReconcileReport] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);

  const loadWallet = async () => {
    try {
      const { data } = await api.get(`/admin/dealers/${dealer.id}/wallet`);
      setWallet(data.wallet);
    } catch { }
  };

  const loadTxns = async (page = 1) => {
    try {
      const { data } = await api.get(`/admin/dealers/${dealer.id}/wallet/transactions?page=${page}&limit=25`);
      setTxns(data);
    } catch { }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadWallet(), loadTxns()]).finally(() => setLoading(false));
  }, [dealer.id]);

  const handleTopUp = async (e) => {
    e.preventDefault();
    const amt = parseFloat(topupAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setTopupLoading(true);
    try {
      const { data } = await api.post(`/admin/dealers/${dealer.id}/wallet/topup`, {
        amount: amt, note: topupNote,
      });
      toast.success(data.message || "Top-up successful");
      setTopupAmount(""); setTopupNote("");
      loadWallet(); loadTxns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Top-up failed");
    } finally { setTopupLoading(false); }
  };

  const handleDeduct = async (e) => {
    e.preventDefault();
    const amt = parseFloat(deductAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!deductReason.trim()) { toast.error("Reason is required"); return; }
    setDeductLoading(true);
    try {
      const { data } = await api.post(`/admin/dealers/${dealer.id}/wallet/deduct`, {
        amount: amt, reason: deductReason,
      });
      toast.success(data.message || "Deduction successful");
      setDeductAmount(""); setDeductReason("");
      loadWallet(); loadTxns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deduction failed");
    } finally { setDeductLoading(false); }
  };

  const handleReconcile = async () => {
    setReconcileLoading(true);
    try {
      const { data } = await api.get(`/admin/dealers/${dealer.id}/wallet/reconcile`);
      setReconcileReport(data);
    } catch (err) {
      toast.error("Reconciliation failed");
    } finally { setReconcileLoading(false); }
  };

  const txnIcon = (type) => {
    if (type === "credit") return <ArrowDownCircle className="w-4 h-4 text-green-500" />;
    if (type === "refund") return <RefreshCw className="w-4 h-4 text-blue-500" />;
    return <ArrowUpCircle className="w-4 h-4 text-red-500" />;
  };

  const txnColor = (type) => {
    if (type === "credit" || type === "refund") return "text-green-600";
    return "text-red-600";
  };

  const txnLabel = (type) => {
    if (type === "credit") return "Credit";
    if (type === "debit") return "Debit";
    if (type === "refund") return "Refund";
    return type;
  };

  const refLabel = (rt) => {
    if (rt === "top_up") return "Top-up";
    if (rt === "recharge") return "Recharge";
    if (rt === "refund") return "Refund";
    if (rt === "manual_adjustment") return "Adjustment";
    return rt || "—";
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-heading font-bold text-xl text-brand-nav flex items-center gap-2">
              <Wallet className="w-5 h-5 text-brand-primary" />
              Dealer Wallet
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">{dealerUser?.name || "Dealer"} · {dealerUser?.mobile || ""}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading wallet...
          </div>
        ) : (
          <>
            {/* Balance Card */}
            <div className="px-6 pt-5 pb-3 shrink-0">
              <div className="bg-gradient-to-br from-brand-primary to-brand-accent rounded-xl p-5 text-white">
                <p className="text-sm opacity-80">Current Balance</p>
                <p className="font-heading font-bold text-3xl mt-1">
                  ₹{wallet ? parseFloat(wallet.balance).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "0.00"}
                </p>
                <div className="flex gap-6 mt-3 text-xs opacity-75">
                  <span>Total Credited: ₹{wallet ? parseFloat(wallet.total_credited).toLocaleString("en-IN") : "0"}</span>
                  <span>Total Debited: ₹{wallet ? parseFloat(wallet.total_debited).toLocaleString("en-IN") : "0"}</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-2 shrink-0">
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                {[
                  { key: "topup", label: "Top Up", icon: Plus },
                  { key: "deduct", label: "Deduct", icon: Minus },
                  { key: "history", label: "History", icon: RefreshCw },
                  { key: "audit", label: "Audit", icon: ShieldCheck },
                ].map(({ key, label, icon: Icon }) => (
                  <button key={key} onClick={() => { setTab(key); if (key === 'audit' && !reconcileReport) handleReconcile(); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-semibold rounded-md transition-colors ${tab === key ? "bg-white text-brand-primary shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
              {tab === "topup" && (
                <form onSubmit={handleTopUp} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Amount (₹)</label>
                    <input type="number" step="0.01" min="1" required value={topupAmount}
                      onChange={(e) => setTopupAmount(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-4 py-3 text-lg font-semibold focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary outline-none"
                      placeholder="Enter amount" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Note / Reference (optional)</label>
                    <input value={topupNote} onChange={(e) => setTopupNote(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary outline-none"
                      placeholder="e.g. Cash received - Receipt #1234" />
                  </div>
                  <button type="submit" disabled={topupLoading}
                    className="w-full flex items-center justify-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50">
                    {topupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Add Balance
                  </button>
                </form>
              )}

              {tab === "deduct" && (
                <form onSubmit={handleDeduct} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Amount (₹)</label>
                    <input type="number" step="0.01" min="1" required value={deductAmount}
                      onChange={(e) => setDeductAmount(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-4 py-3 text-lg font-semibold focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none"
                      placeholder="Enter amount" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Reason (required)</label>
                    <textarea required rows={2} value={deductReason} onChange={(e) => setDeductReason(e.target.value)}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none"
                      placeholder="Reason for deduction..." />
                  </div>
                  <button type="submit" disabled={deductLoading}
                    className="w-full flex items-center justify-center gap-2 bg-red-500 hover:bg-red-600 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50">
                    {deductLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Minus className="w-4 h-4" />}
                    Deduct Balance
                  </button>
                </form>
              )}

              {tab === "history" && (
                <div>
                  {txns.items.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No transactions yet</p>
                  ) : (
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                              <th className="text-left px-3 py-2.5">#</th>
                              <th className="text-left px-3 py-2.5">Date</th>
                              <th className="text-left px-3 py-2.5">Type</th>
                              <th className="text-left px-3 py-2.5">Reference</th>
                              <th className="text-right px-3 py-2.5">Opening</th>
                              <th className="text-right px-3 py-2.5 text-green-600">Credit</th>
                              <th className="text-right px-3 py-2.5 text-red-600">Debit</th>
                              <th className="text-right px-3 py-2.5">Closing</th>
                              <th className="text-left px-3 py-2.5">Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {txns.items.map((t) => {
                              const amt = parseFloat(t.amount || 0);
                              const opening = parseFloat(t.balance_before ?? 0);
                              const closing = parseFloat(t.balance_after ?? 0);
                              const isCredit = t.type === "credit" || t.type === "refund";
                              return (
                                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors">
                                  <td className="px-3 py-2.5 text-slate-400 font-mono">{t.seq_no || "—"}</td>
                                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">
                                    {t.created_at ? (
                                      <>
                                        <span className="font-medium">{new Date(t.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                                        <span className="text-slate-400 ml-1">{new Date(t.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                                      </>
                                    ) : "—"}
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${
                                      isCredit ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"
                                    }`}>
                                      {t.type === "credit" && <ArrowDownCircle className="w-3 h-3" />}
                                      {t.type === "debit" && <ArrowUpCircle className="w-3 h-3" />}
                                      {t.type === "refund" && <RefreshCw className="w-3 h-3" />}
                                      {txnLabel(t.type)}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2.5">
                                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{refLabel(t.reference_type)}</span>
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono text-slate-600">₹{opening.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                                  <td className="px-3 py-2.5 text-right font-mono">
                                    {isCredit ? <span className="font-semibold text-green-600">+₹{amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span> : <span className="text-slate-300">—</span>}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono">
                                    {!isCredit ? <span className="font-semibold text-red-600">−₹{amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span> : <span className="text-slate-300">—</span>}
                                  </td>
                                  <td className="px-3 py-2.5 text-right font-mono font-bold text-brand-nav">₹{closing.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                                  <td className="px-3 py-2.5 text-slate-500 max-w-[120px] truncate" title={t.note || ""}>{t.note || "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {/* Pagination */}
                  {txns.total > 0 && (
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-[10px] text-slate-400">
                        {(txns.page - 1) * txns.limit + 1}–{Math.min(txns.page * txns.limit, txns.total)} of {txns.total}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <button disabled={txns.page <= 1} onClick={() => loadTxns(txns.page - 1)}
                          className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40">Prev</button>
                        <span className="text-xs text-slate-500">{txns.page}/{Math.ceil(txns.total / txns.limit)}</span>
                        <button disabled={txns.page >= Math.ceil(txns.total / txns.limit)} onClick={() => loadTxns(txns.page + 1)}
                          className="text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40">Next</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === "audit" && (
                <div>
                  {reconcileLoading ? (
                    <div className="text-center py-8 text-slate-400">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Running ledger verification...
                    </div>
                  ) : reconcileReport ? (
                    <div className="space-y-4">
                      {/* Status banner */}
                      <div className={`rounded-lg p-4 flex items-start gap-3 ${
                        reconcileReport.ok ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                      }`}>
                        <ShieldCheck className={`w-6 h-6 mt-0.5 ${reconcileReport.ok ? 'text-green-600' : 'text-red-600'}`} />
                        <div>
                          <p className={`font-bold text-sm ${reconcileReport.ok ? 'text-green-700' : 'text-red-700'}`}>
                            {reconcileReport.ok ? 'Ledger Verified — All Balances Match' : 'Discrepancies Found!'}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">{reconcileReport.message}</p>
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-[10px] text-slate-400 uppercase font-bold">Stored Balance</p>
                          <p className="font-bold text-lg text-slate-800">₹{reconcileReport.stored_balance?.toLocaleString('en-IN', {minimumFractionDigits: 2})}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-[10px] text-slate-400 uppercase font-bold">Ledger Balance</p>
                          <p className={`font-bold text-lg ${reconcileReport.stored_balance === reconcileReport.ledger_balance ? 'text-green-700' : 'text-red-700'}`}>
                            ₹{reconcileReport.ledger_balance?.toLocaleString('en-IN', {minimumFractionDigits: 2})}
                          </p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-[10px] text-slate-400 uppercase font-bold">Total Credits</p>
                          <p className="font-semibold text-green-600">₹{reconcileReport.total_credits?.toLocaleString('en-IN')}</p>
                        </div>
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-[10px] text-slate-400 uppercase font-bold">Total Debits</p>
                          <p className="font-semibold text-red-600">₹{reconcileReport.total_debits?.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-400">Transactions: {reconcileReport.total_transactions} | Seq: {reconcileReport.txn_seq} | Checked: {reconcileReport.checked_at ? new Date(reconcileReport.checked_at).toLocaleString('en-IN') : ''}</p>

                      {/* Issues */}
                      {reconcileReport.issues?.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-bold text-slate-500 uppercase">Issues Found</p>
                          {reconcileReport.issues.map((issue, i) => (
                            <div key={i} className={`rounded-lg p-3 text-xs border ${
                              issue.severity === 'CRITICAL' ? 'bg-red-50 border-red-200 text-red-700'
                                : 'bg-amber-50 border-amber-200 text-amber-700'
                            }`}>
                              <span className="font-bold">[{issue.severity}] {issue.check}</span>
                              <p className="mt-0.5 opacity-80">{issue.detail}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      <button onClick={handleReconcile}
                        className="w-full text-sm font-semibold text-brand-primary hover:bg-brand-primary/5 rounded-lg py-2 transition-colors">
                        ↻ Re-run Verification
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
